// How many conversations are unread, for the two Messages rows in the sidebar
// (PRD 68 §5.1 "with an unread count", §5.2 "Messages — things to read, with
// an unread badge").
//
// ONE COUNT PER SIDE, SHARED, the shape notificationCountStore uses next door:
// the sidebar asks, and the thread screens change the answer by opening a
// thread (opening is reading). They call the refresh below after they load,
// so the badge drops without a reload.
//
// A FAILED FETCH LEAVES THE LAST ANSWER, for the same reason as the
// notification badge: flickering to zero because one request failed tells
// somebody they have nothing to read when they do. An api that predates the
// count endpoint therefore shows no badge at all, which is what it showed
// before.

import { firmService } from './firmService';
import { clientMessagingService, messagingService } from './messagingService';

export interface UnreadMessageCounts {
  /** Threads the client has written in since this person last read them. */
  firm: number;
  /** Threads the firm has written in, on the ACTIVE business's inbox. */
  client: number;
}

type Listener = (counts: UnreadMessageCounts) => void;

let counts: UnreadMessageCounts = { firm: 0, client: 0 };
const listeners = new Set<Listener>();
let firmInFlight: Promise<void> | null = null;
let clientInFlight: Promise<void> | null = null;
// The practice every HQ screen opens (the first `GET /firm/firms` returns),
// asked once per session rather than on every refresh.
let firmIdPromise: Promise<string | null> | null = null;

function publish(patch: Partial<UnreadMessageCounts>) {
  counts = { ...counts, ...patch };
  listeners.forEach(listener => listener(counts));
}

export function getUnreadMessageCounts(): UnreadMessageCounts {
  return counts;
}

export function subscribeUnreadMessageCounts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function firmId(): Promise<string | null> {
  if (!firmIdPromise) {
    firmIdPromise = Promise.resolve()
      .then(() => firmService.listFirms())
      .then(list => list[0]?.id ?? null)
      .catch(() => {
        // Forgotten, so the next refresh asks again.
        firmIdPromise = null;
        return null;
      });
  }
  return firmIdPromise;
}

/** The practice side. Concurrent callers share one request. */
export function refreshFirmUnreadMessages(): Promise<void> {
  if (firmInFlight) return firmInFlight;
  // Through a `then`, so a call that throws synchronously is a failure like
  // any other — never an exception thrown into the screen that asked.
  firmInFlight = Promise.resolve()
    .then(firmId)
    .then(id => (id ? messagingService.unreadCount(id) : null))
    .then(count => { if (count !== null) publish({ firm: Math.max(0, count) }); })
    .catch(() => undefined)
    .finally(() => { firmInFlight = null; });
  return firmInFlight;
}

/** The client side, for whichever business is active. */
export function refreshClientUnreadMessages(): Promise<void> {
  if (clientInFlight) return clientInFlight;
  clientInFlight = Promise.resolve()
    .then(() => clientMessagingService.unreadCount())
    .then(count => publish({ client: Math.max(0, count) }))
    .catch(() => undefined)
    .finally(() => { clientInFlight = null; });
  return clientInFlight;
}

/** Signed out: nothing is anybody's to read, and the practice is forgotten. */
export function clearUnreadMessages(): void {
  firmIdPromise = null;
  publish({ firm: 0, client: 0 });
}
